import type { PlaycastSystemDeviceChange } from '@playcastdotio/CoreTypes';
import type { IdleExclusionHostEntry } from '@playcastdotio/IdleExclusionTypes';
import type {
  LatencyMapAppTarget,
  LatencyMapBody,
  PlaycastMessage,
  PlaycastMessageSource,
  PlaycastMessageSystemDie,
  PlaycastMessageSystemGetAutomaticUpdatesEnabled,
  PlaycastMessageSystemGetAutomaticUpdatesEnabledReply,
  PlaycastMessageSystemGetCurrentUserInfo,
  PlaycastMessageSystemGetDriverStatus,
  PlaycastMessageSystemGetSanitizationState,
  PlaycastMessageSystemGetRestartRequired,
  PlaycastMessageSystemGetScoringMetrics,
  PlaycastMessageSystemGetScoringMetricsReply,
  GamingScoreRawMetrics,
  PlaycastMessageSystemUpdateHardwareDatabase,
  PlaycastMessageSystemUpdateHardwareDatabaseReply,
  PlaycastMessageSystemGetMachineId,
  PlaycastMessageSystemGetMachineIdAttestation,
  PlaycastMessageSystemSetMachineIdAttestationMetadata,
  PlaycastMessageSystemGetSchemas,
  PlaycastMessageSystemGetUser,
  PlaycastMessageSystemSecureStorageGet,
  PlaycastMessageSystemSecureStorageGetReply,
  PlaycastMessageSystemSecureStorageSet,
  PlaycastMessageSystemSecureStorageSetReply,
  PlaycastMessageSystemSecureStorageRemove,
  PlaycastMessageSystemSecureStorageRemoveReply,
  PlaycastMessageSystemSetAutomaticUpdate,
  PlaycastMessageSystemSetUnattendedConfiguration,
  PlaycastMessageSystemSetIdleExclusionList,
  PlaycastMessageSystemSetNonsoleEntryGateConfiguration,
  PlaycastMessageSystemLatencyMap,
  PlaycastMessageSystemLatencyMapReply,
  PlaycastMessageSystemSetDeviceChange,
  PlaycastMessageSystemTestLogger,
  PlaycastMessageSystemLogOffSession,
  PlaycastMessageSystemSetVpnAccessGranted,
  PlaycastMessageSystemSetVpnHostname,
  PlaycastMessageSystemLaunchElevatedProcess,
  PlaycastMessageSystemLaunchElevatedPowerShellScript,
  PlaycastMessageSystemCancelProvisioningScript,
  PlaycastMessageSystemReboot,
  PlaycastMessageSystemSubmitInstallRequest,
} from '../types';
import { formatMessage } from './_util/formatMessage';

const target = 'system';

/**
 * @experimental This is an experimental feature and may be changed in the future.
 */
export const getDriverStatus = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemGetDriverStatus> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetDriverStatus = {
    target,
    action: 'getDriverStatus',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetDriverStatus>;
};

export const getSanitizationState = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemGetSanitizationState> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetSanitizationState = {
    target,
    action: 'getSanitizationState',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetSanitizationState>;
};

/**
 * Requests the host's current restart-required state (CommonLib::RebootPolicy).
 * Used by node-service to report deferred-restart status up to realtime-api on
 * external hosts, which never reboot automatically.
 */
export const getRestartRequired = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemGetRestartRequired> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetRestartRequired = {
    target,
    action: 'getRestartRequired',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetRestartRequired>;
};

/**
 * @experimental This is an experimental feature and may be changed in the future.
 */
export const getUser = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemGetUser> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetUser = {
    target,
    action: 'getUser',
    isReply: false,
    message: {
      username: '',
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetUser>;
};

/**
 * @experimental This is an experimental feature and may be changed in the future.
 */
export const die = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemDie> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemDie = {
    target,
    action: 'die',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemDie>;
};

/**
 * @experimental This is an experimental feature and may be changed in the future.
 */
export const getSchemas = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemGetSchemas> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetSchemas = {
    target,
    action: 'getSchemas',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetSchemas>;
};

export const setDeviceChange = (
  hostId: string,
  deviceChange: PlaycastSystemDeviceChange,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetDeviceChange> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageSystemSetDeviceChange = {
    target,
    action: 'setDeviceChange',
    isReply: false,
    message: deviceChange,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetDeviceChange>;
};

export const getMachineId = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemGetMachineId> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetMachineId = {
    target,
    action: 'getMachineId',
    isReply: false,
    message: {},
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetMachineId>;
};

export const getMachineIdAttestation = (nonce: string, clerkUserId: string, userAuth?: string, timestamp?: number): PlaycastMessage<PlaycastMessageSystemGetMachineIdAttestation> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetMachineIdAttestation = {
    target,
    action: 'getMachineIdAttestation',
    isReply: false,
    message: {
      nonce,
      clerkUserId,
      ...(timestamp !== undefined && { timestamp }),
    },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetMachineIdAttestation>;
};

export const setMachineIdAttestationMetadata = (
  clerkUserId: string,
  attestedAt: number,
  expiresAt?: number,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetMachineIdAttestationMetadata> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSetMachineIdAttestationMetadata = {
    target,
    action: 'setMachineIdAttestationMetadata',
    isReply: false,
    message: {
      clerkUserId,
      attestedAt,
      expiresAt,
    },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetMachineIdAttestationMetadata>;
};

export const getCurrentUserInfo = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemGetCurrentUserInfo> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetCurrentUserInfo = {
    target,
    action: 'getCurrentUserInfo',
    isReply: false,
    message: {},
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetCurrentUserInfo>;
};

export const testLogger = (userAuth?: string): PlaycastMessage<PlaycastMessageSystemTestLogger> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemTestLogger = {
    target,
    action: 'testLogger',
    isReply: false,
    message: {},
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemTestLogger>;
};

export const latencyMap = (
  source: PlaycastMessageSource,
  appTarget: LatencyMapAppTarget,
  steps: LatencyMapBody[],
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemLatencyMap> => {
  const message: PlaycastMessageSystemLatencyMap = {
    target,
    action: 'latencyMap',
    isReply: false,
    message: {
      appTarget,
      steps,
    },
  };
  return formatMessage('host', source, message, userAuth) as PlaycastMessage<PlaycastMessageSystemLatencyMap>;
};

export const latencyMapReply = (
  source: PlaycastMessageSource,
  appTarget: LatencyMapAppTarget,
  steps: LatencyMapBody[],
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemLatencyMapReply> => {
  const message: PlaycastMessageSystemLatencyMapReply = {
    target,
    action: 'latencyMap',
    isReply: true,
    message: {
      appTarget,
      steps,
    },
  };
  return formatMessage('host', source, message, userAuth) as PlaycastMessage<PlaycastMessageSystemLatencyMapReply>;
};

export const logOffSession = (
  username:  string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemLogOffSession> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemLogOffSession = {
    target,
    action: 'logOffSession',
    isReply: false,
    message: {
      username,
    },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemLogOffSession>;
};

export const getAutomaticUpdatesEnabled = (
  hostId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemGetAutomaticUpdatesEnabled> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetAutomaticUpdatesEnabled = {
    target,
    action: 'getAutomaticUpdatesEnabled',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetAutomaticUpdatesEnabled>;
};

export const getAutomaticUpdatesEnabledReply = (
  hostId: string,
  automaticUpdatesEnabled: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemGetAutomaticUpdatesEnabledReply> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetAutomaticUpdatesEnabledReply = {
    target,
    action: 'getAutomaticUpdatesEnabled',
    isReply: true,
    message: { automaticUpdatesEnabled },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetAutomaticUpdatesEnabledReply>;
};

export const setAutomaticUpdatesEnabled = (
  hostId: string,
  automaticUpdatesEnabled: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetAutomaticUpdate> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSetAutomaticUpdate = {
    target,
    action: 'setAutomaticUpdatesEnabled',
    isReply: false,
    message: { automaticUpdatesEnabled },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetAutomaticUpdate>;
};

/**
 * Playjector emits raw metrics only; the TS layer is responsible for scoring.
 * See `apps/realtime-api/src/coordinators/gamingScore/compute/computeGamingScore.ts`.
 */
export const getScoringMetrics = (
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemGetScoringMetrics> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetScoringMetrics = {
    target,
    action: 'getScoringMetrics',
    isReply: false,
    message: {},
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetScoringMetrics>;
};

export const getScoringMetricsReply = (
  hostId: string,
  payload: GamingScoreRawMetrics,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemGetScoringMetricsReply> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemGetScoringMetricsReply = {
    target,
    action: 'getScoringMetrics',
    isReply: true,
    message: payload,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemGetScoringMetricsReply>;
};

export const updateHardwareDatabase = (
  entries: { cpu?: any[]; gpu?: any[] },
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemUpdateHardwareDatabase> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemUpdateHardwareDatabase = {
    target,
    action: 'updateHardwareDatabase',
    isReply: false,
    message: {
      entries,
    },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemUpdateHardwareDatabase>;
};

export const secureStorageGet = (key: string, userAuth?: string): PlaycastMessage<PlaycastMessageSystemSecureStorageGet> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSecureStorageGet = {
    target,
    action: 'secureStorageGet',
    isReply: false,
    message: { key },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSecureStorageGet>;
};

export const secureStorageGetReply = (
  hostId: string,
  key: string,
  succeeded: boolean,
  value: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSecureStorageGetReply> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSecureStorageGetReply = {
    target,
    action: 'secureStorageGet',
    isReply: true,
    message: { key, succeeded, value },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSecureStorageGetReply>;
};

export const secureStorageSet = (key: string, value: string, userAuth?: string): PlaycastMessage<PlaycastMessageSystemSecureStorageSet> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSecureStorageSet = {
    target,
    action: 'secureStorageSet',
    isReply: false,
    message: { key, value },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSecureStorageSet>;
};

export const secureStorageSetReply = (
  hostId: string,
  key: string,
  succeeded: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSecureStorageSetReply> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSecureStorageSetReply = {
    target,
    action: 'secureStorageSet',
    isReply: true,
    message: { key, succeeded },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSecureStorageSetReply>;
};

export const secureStorageRemove = (key: string, userAuth?: string): PlaycastMessage<PlaycastMessageSystemSecureStorageRemove> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSecureStorageRemove = {
    target,
    action: 'secureStorageRemove',
    isReply: false,
    message: { key },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSecureStorageRemove>;
};

export const secureStorageRemoveReply = (
  hostId: string,
  key: string,
  succeeded: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSecureStorageRemoveReply> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSecureStorageRemoveReply = {
    target,
    action: 'secureStorageRemove',
    isReply: true,
    message: { key, succeeded },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSecureStorageRemoveReply>;
};

export const setUnattendedConfiguration = (
  idleTimeMs = 3600000,
  enabled: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetUnattendedConfiguration> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSetUnattendedConfiguration = {
    target,
    action: 'setUnattendedConfiguration',
    isReply: false,
    message: { idleTimeMs, enabled },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetUnattendedConfiguration>;
};

export const setIdleExclusionList = (
  entries: IdleExclusionHostEntry[],
  updatedAt: number,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetIdleExclusionList> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSetIdleExclusionList = {
    target,
    action: 'setIdleExclusionList',
    isReply: false,
    message: { entries, updatedAt },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetIdleExclusionList>;
};

export const setNonsoleEntryGateConfiguration = (
  disabledGateIds: string[],
  updatedAt: number,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetNonsoleEntryGateConfiguration> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSetNonsoleEntryGateConfiguration = {
    target,
    action: 'setNonsoleEntryGateConfiguration',
    isReply: false,
    message: { disabledGateIds, updatedAt },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetNonsoleEntryGateConfiguration>;
};

export const setVpnAccessGranted = (
  vpnAccessGranted: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetVpnAccessGranted> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSetVpnAccessGranted = {
    target,
    action: 'setVpnAccessGranted',
    isReply: false,
    message: {
      vpnAccessGranted,
    },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetVpnAccessGranted>;
};

export const setVpnHostname = (
  vpnHostname: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSetVpnHostname> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSetVpnHostname= {
    target,
    action: 'setVpnHostname',
    isReply: false,
    message: {
      vpnHostname,
    },
  };
  return formatMessage('host', messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSetVpnHostname>;
};

/**
 * Request a system reboot via PlayJector (InitiateSystemShutdownEx).
 * Does not use WTSQueryUserToken / elevated session launch — works when launchElevatedProcess cannot.
 */
export const reboot = (
  hostId: string,
  options: { timeOutLength: number; forceAppsClosed: boolean },
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemReboot> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemReboot = {
    target,
    action: 'reboot',
    isReply: false,
    message: {
      timeOutLength: options.timeOutLength,
      forceAppsClosed: options.forceAppsClosed,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemReboot>;
};

/**
 * Request a remote-initiated PlaycastService update check via embedded PlayJector hub.
 * PlaycastService calls InstallManager in-process with ForceManifestCheck + Silent + RemoteInitiated.
 */
export const submitInstallRequest = (
  hostId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageSystemSubmitInstallRequest> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemSubmitInstallRequest = {
    target,
    action: 'submitInstallRequest',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemSubmitInstallRequest>;
};

export const launchElevatedProcess = (
  hostId: string,
  processPath: string,
  processArguments = '',
  workingDirectory?: string,
  userAuth?: string,
  options?: { fromAdminSession?: boolean },
): PlaycastMessage<PlaycastMessageSystemLaunchElevatedProcess> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemLaunchElevatedProcess = {
    target,
    action: 'launchElevatedProcess',
    isReply: false,
    message: {
      path: processPath,
      arguments: processArguments,
      workingDirectory,
      ...(options?.fromAdminSession === true ? { fromAdminSession: true } : {}),
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemLaunchElevatedProcess>;
};

export const launchElevatedPowerShellScript = (
  hostId: string,
  script: string,
  workingDirectory?: string,
  userAuth?: string,
  options?: {
    fromAdminSession?: boolean;
    fromProvisioning?: boolean;
    scriptSignature?: string;
    timeoutSeconds?: number;
  },
): PlaycastMessage<PlaycastMessageSystemLaunchElevatedPowerShellScript> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemLaunchElevatedPowerShellScript = {
    target,
    action: 'launchElevatedPowerShellScript',
    isReply: false,
    message: {
      script,
      workingDirectory,
      ...(options?.fromAdminSession === true ? { fromAdminSession: true } : {}),
      ...(options?.fromProvisioning === true ? { fromProvisioning: true } : {}),
      ...(options?.scriptSignature ? { scriptSignature: options.scriptSignature } : {}),
      ...(options?.timeoutSeconds ? { timeoutSeconds: options.timeoutSeconds } : {}),
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageSystemLaunchElevatedPowerShellScript>;
};

export const cancelProvisioningScript = (
  hostId: string,
  options?: {
    exitGuestMode?: boolean;
    rebootIfSafe?: boolean;
  },
): PlaycastMessage<PlaycastMessageSystemCancelProvisioningScript> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageSystemCancelProvisioningScript = {
    target,
    action: 'cancelProvisioningScript',
    isReply: false,
    message: {
      ...(options?.exitGuestMode ? { exitGuestMode: true } : {}),
      ...(options?.rebootIfSafe ? { rebootIfSafe: true } : {}),
    },
  };
  return formatMessage(hostId, messageSource, message) as PlaycastMessage<PlaycastMessageSystemCancelProvisioningScript>;
};
